import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const PriceListEditor = ({ priceList, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    id: priceList?.id || null,
    name: priceList?.name || '',
    description: priceList?.description || '',
    is_active: priceList?.is_active ?? true,
    valid_from: priceList?.valid_from?.split('T')?.[0] || new Date()?.toISOString()?.split('T')?.[0],
    valid_until: priceList?.valid_until?.split('T')?.[0] || '',
    items: priceList?.price_list_items || []
  });

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemForm, setItemForm] = useState({
    product_id: '',
    discount_type: 'percentage',
    discount_value: 0,
    custom_price: null,
    notes: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('products')
        ?.select('id, name, sku, price')
        ?.order('name');

      if (error) throw error;

      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error?.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!itemForm?.product_id) {
      alert('Please select a product');
      return;
    }

    const product = products?.find(p => p?.id === itemForm?.product_id);
    if (!product) return;

    // Check if product already exists
    if (formData?.items?.some(item => item?.product_id === itemForm?.product_id)) {
      alert('This product is already in the price list');
      return;
    }

    const newItem = {
      product_id: itemForm?.product_id,
      product: product,
      discount_type: itemForm?.discount_type,
      discount_value: parseFloat(itemForm?.discount_value) || 0,
      custom_price: itemForm?.custom_price ? parseFloat(itemForm?.custom_price) : null,
      notes: itemForm?.notes
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev?.items, newItem]
    }));

    // Reset form
    setItemForm({
      product_id: '',
      discount_type: 'percentage',
      discount_value: 0,
      custom_price: null,
      notes: ''
    });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev?.items?.filter((_, i) => i !== index)
    }));
  };

  const calculateDiscountedPrice = (item) => {
    const basePrice = item?.product?.price || 0;
    
    if (item?.custom_price) {
      return parseFloat(item?.custom_price);
    }

    if (item?.discount_type === 'percentage') {
      return basePrice * (1 - item?.discount_value / 100);
    } else {
      return basePrice - item?.discount_value;
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!formData?.name) {
      alert('Please enter a price list name');
      return;
    }

    if (formData?.items?.length === 0) {
      alert('Please add at least one product to the price list');
      return;
    }

    onSave(formData);
  };

  const availableProducts = products?.filter(
    p => !formData?.items?.some(item => item?.product_id === p?.id)
  );

  return (
    <form onSubmit={handleSubmit} className="bg-card rounded-xl border border-border shadow-elevation-1 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="FileText" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            {formData?.id ? 'Edit Price List' : 'Create New Price List'}
          </h2>
          <p className="text-sm text-muted-foreground">Configure pricing rules and discounts</p>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Price List Name"
          placeholder="e.g., VIP Customer Pricing"
          required
          value={formData?.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
        />
        <div className="flex items-center gap-3 pt-8">
          <input
            type="checkbox"
            id="is_active"
            checked={formData?.is_active}
            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e?.target?.checked }))}
            className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring"
          />
          <label htmlFor="is_active" className="text-sm font-medium text-foreground">
            Active Price List
          </label>
        </div>
      </div>

      <Input
        label="Description"
        placeholder="Optional description"
        value={formData?.description}
        onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          type="date"
          label="Valid From"
          required
          value={formData?.valid_from}
          onChange={(e) => setFormData(prev => ({ ...prev, valid_from: e?.target?.value }))}
        />
        <Input
          type="date"
          label="Valid Until (Optional)"
          value={formData?.valid_until}
          onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e?.target?.value }))}
        />
      </div>

      {/* Add Product Section */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Add Products</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Select
            label="Product"
            placeholder="Select product"
            searchable
            options={availableProducts?.map(p => ({
              value: p?.id,
              label: p?.name,
              description: `${p?.sku} - $${p?.price}`
            }))}
            value={itemForm?.product_id}
            onChange={(value) => setItemForm(prev => ({ ...prev, product_id: value }))}
            loading={loading}
          />
          <Select
            label="Discount Type"
            options={[
              { value: 'percentage', label: 'Percentage' },
              { value: 'fixed_amount', label: 'Fixed Amount' }
            ]}
            value={itemForm?.discount_type}
            onChange={(value) => setItemForm(prev => ({ ...prev, discount_type: value }))}
          />
          <Input
            type="number"
            label="Discount Value"
            placeholder="0"
            min="0"
            step="0.01"
            value={itemForm?.discount_value}
            onChange={(e) => setItemForm(prev => ({ ...prev, discount_value: e?.target?.value }))}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Input
            type="number"
            label="Custom Price (Optional)"
            placeholder="Override with custom price"
            min="0"
            step="0.01"
            value={itemForm?.custom_price || ''}
            onChange={(e) => setItemForm(prev => ({ ...prev, custom_price: e?.target?.value }))}
          />
          <Input
            label="Notes (Optional)"
            placeholder="Additional notes"
            value={itemForm?.notes}
            onChange={(e) => setItemForm(prev => ({ ...prev, notes: e?.target?.value }))}
          />
        </div>
        <div className="mt-4">
          <Button
            type="button"
            onClick={handleAddItem}
            iconName="Plus"
            iconPosition="left"
            variant="outline"
          >
            Add Product
          </Button>
        </div>
      </div>

      {/* Products Table */}
      {formData?.items?.length > 0 && (
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Products in Price List</h3>
          <div className="bg-background rounded-lg border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Product</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Base Price</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Discount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Final Price</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {formData?.items?.map((item, index) => (
                  <tr key={index} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{item?.product?.name}</p>
                        <p className="text-xs text-muted-foreground">{item?.product?.sku}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      ${parseFloat(item?.product?.price || 0)?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      {item?.custom_price ? (
                        <span className="text-sm text-secondary">Custom Price</span>
                      ) : (
                        <span className="text-sm text-foreground">
                          {item?.discount_type === 'percentage' 
                            ? `${item?.discount_value}%` 
                            : `$${item?.discount_value}`}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-success">
                      ${calculateDiscountedPrice(item)?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(index)}
                        iconName="Trash2"
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" iconName="Save" iconPosition="left">
          Save Price List
        </Button>
      </div>
    </form>
  );
};

export default PriceListEditor;